/**
 * Active factory project under the signed-in team (beatly / devtools partition).
 * Persists like the board switcher; all factory API calls read projectId from here.
 */

import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { storiesApi, type StoryProject } from '../services/devtoolsApi';

export const PROJECT_STORAGE_KEY = 'devtools.activeProjectId';

export type ProjectContextValue = {
  projects: StoryProject[];
  projectId: string;
  setProjectId: (id: string) => void;
  isLoading: boolean;
  project: StoryProject | null;
};

export const ProjectContext = createContext<ProjectContextValue | undefined>(undefined);

function readStoredProjectId(): string {
  try {
    return localStorage.getItem(PROJECT_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

/** Prefer kitchen-entrance ?project= (or storage written by AuthContext) once. */
function readInitialProjectId(): string {
  if (typeof window !== 'undefined') {
    try {
      const fromQuery = new URLSearchParams(window.location.search).get('project')?.trim();
      if (fromQuery) {
        localStorage.setItem(PROJECT_STORAGE_KEY, fromQuery);
        return fromQuery;
      }
    } catch {
      /* ignore */
    }
  }
  return readStoredProjectId();
}

function pickDefaultProjectId(projects: StoryProject[], stored: string): string {
  if (stored && projects.some((p) => p.project_id === stored)) return stored;
  const beatly = projects.find((p) => p.name === 'beatly' || p.project_id === 'beatly-dpr');
  if (beatly) return beatly.project_id;
  return projects[0]?.project_id || '';
}

type ProjectProviderProps = {
  children: ReactNode;
  /** Test override — skip catalog fetch and use this fixed project id. */
  initialProjectId?: string;
  initialProjects?: StoryProject[];
};

/** Fixed project scope for Vitest (no catalog fetch). */
function FixedProjectProvider({
  children,
  projectId,
  projects,
}: {
  children: ReactNode;
  projectId: string;
  projects: StoryProject[];
}) {
  const value = useMemo<ProjectContextValue>(
    () => ({
      projects,
      projectId,
      setProjectId: () => undefined,
      isLoading: false,
      project: projects.find((p) => p.project_id === projectId) || projects[0] || null,
    }),
    [projects, projectId],
  );
  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

/** Live catalog + localStorage persistence for the factory project switcher. */
function LiveProjectProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [projectId, setProjectIdState] = useState(readInitialProjectId);

  const projectsQ = useQuery({
    queryKey: ['factory-projects'],
    queryFn: () => storiesApi.listProjects(),
  });
  const projects = useMemo(
    () => projectsQ.data?.projects || [],
    [projectsQ.data?.projects],
  );

  // Resolve a valid project once the catalog loads (stored → beatly → first).
  useEffect(() => {
    if (!projects.length) return;
    const next = pickDefaultProjectId(projects, projectId);
    if (next && next !== projectId) {
      setProjectIdState(next);
      try {
        localStorage.setItem(PROJECT_STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
    }
  }, [projects, projectId]);

  const setProjectId = useCallback(
    (id: string) => {
      if (!id || id === projectId) return;
      setProjectIdState(id);
      try {
        localStorage.setItem(PROJECT_STORAGE_KEY, id);
      } catch {
        /* ignore */
      }
      // Drop board preference — boards are project-scoped after ensure.
      try {
        localStorage.removeItem('devtools.selectedBoardId');
      } catch {
        /* ignore */
      }
      void queryClient.invalidateQueries({ queryKey: ['board'] });
      void queryClient.invalidateQueries({ queryKey: ['board-meta'] });
      void queryClient.invalidateQueries({ queryKey: ['roadmap'] });
      void queryClient.invalidateQueries({ queryKey: ['automations'] });
      void queryClient.invalidateQueries({ queryKey: ['coding-agents'] });
      void queryClient.invalidateQueries({ queryKey: ['epics'] });
      void queryClient.invalidateQueries({ queryKey: ['tracker-boards'] });
      void queryClient.invalidateQueries({ queryKey: ['support-slack'] });
    },
    [projectId, queryClient],
  );

  const project = useMemo(
    () => projects.find((p) => p.project_id === projectId) || null,
    [projects, projectId],
  );

  const value = useMemo<ProjectContextValue>(
    () => ({
      projects,
      projectId,
      setProjectId,
      isLoading: projectsQ.isLoading,
      project,
    }),
    [projects, projectId, setProjectId, projectsQ.isLoading, project],
  );

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

/** Loads team projects and holds the active factory projectId for shell + pages. */
export function ProjectProvider({
  children,
  initialProjectId,
  initialProjects,
}: ProjectProviderProps) {
  if (initialProjectId) {
    const projects = initialProjects || [
      {
        project_id: initialProjectId,
        team_id: 'admin-beatly-tea',
        name: 'beatly',
      },
    ];
    return (
      <FixedProjectProvider projectId={initialProjectId} projects={projects}>
        {children}
      </FixedProjectProvider>
    );
  }
  return <LiveProjectProvider>{children}</LiveProjectProvider>;
}
